import { ReactElement, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Checkbox,
  FormControlLabel,
  Box,
  Link,
  Stack,
} from "@mui/material";

interface TermsAcceptanceDialogProps {
  open: boolean;
  onAccept: () => void;
  onDecline: () => void;
}

function TermsAcceptanceDialog({
  open,
  onAccept,
  onDecline,
}: TermsAcceptanceDialogProps): ReactElement {
  const [accepted, setAccepted] = useState<boolean>(false);

  const handleAccept = (): void => {
    if (accepted) {
      onAccept();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onDecline}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle>
        <Typography variant="h6" fontWeight="bold">
          Terms of Service & Privacy Policy
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            Welcome to Budget-Trace! Before you continue, please review and accept our Terms of Service and Privacy Policy.
          </Typography>

          <Box
            sx={{
              maxHeight: 300,
              overflow: "auto",
              border: 1,
              borderColor: "divider",
              borderRadius: 1,
              p: 2,
              bgcolor: "background.default",
            }}
          >
            <Typography variant="body2" component="div" sx={{ whiteSpace: "pre-wrap" }}>
              {/* Terms content will go here - you can replace this with actual terms */}
              <strong>Terms of Service</strong>
              {"\n\n"}
              By using Budget-Trace, you agree to the following terms:
              {"\n\n"}
              1. You are responsible for maintaining the confidentiality of your account.
              {"\n"}
              2. You agree to use the service in compliance with all applicable laws.
              {"\n"}
              3. We reserve the right to modify or discontinue the service at any time.
              {"\n"}
              4. You are responsible for all activities that occur under your account.
              {"\n\n"}
              <strong>Privacy Policy</strong>
              {"\n\n"}
              We respect your privacy and are committed to protecting your personal data:
              {"\n\n"}
              1. We collect only the information necessary to provide our services.
              {"\n"}
              2. Your financial data is encrypted and stored securely.
              {"\n"}
              3. We do not share your personal information with third parties without your consent.
              {"\n"}
              4. You can request deletion of your data at any time.
              {"\n\n"}
              For the complete Terms of Service and Privacy Policy, please visit our website.
            </Typography>
          </Box>

          <FormControlLabel
            control={
              <Checkbox
                checked={accepted}
                onChange={(e) => setAccepted(e.target.checked)}
                required
              />
            }
            label={
              <Typography variant="body2">
                I have read and agree to the{" "}
                <Link href="#" target="_blank" rel="noopener noreferrer">
                  Terms of Service
                </Link>{" "}
                and{" "}
                <Link href="#" target="_blank" rel="noopener noreferrer">
                  Privacy Policy
                </Link>
              </Typography>
            }
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onDecline} color="inherit">
          Decline
        </Button>
        <Button
          onClick={handleAccept}
          variant="contained"
          disabled={!accepted}
        >
          Accept & Continue
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default TermsAcceptanceDialog;
